/* Item 15 -- separate size selections for sets.

   Every surface used to loop product.variants and print variant.title, which for a
   two-option product is the combined "8 / 10" the client flagged (a 5x5 grid of
   confusing numbers). Each surface now renders one control per product OPTION, using
   the option's own name as the heading, and resolves the chosen combination back to a
   variant id through the helpers below.

   The resolution rules live here, in one file, rather than being reimplemented on each
   surface -- same reasoning as snippets/high-thread-whatsapp-modal.liquid being the one
   source of truth for the SMS modal.

   Loaded by sections/interactive-environment-1.liquid, high-thread-archive.liquid,
   high-thread-legal.liquid (all three load interactive-environments.js, which consumes
   this), high-thread-mobile-home.liquid and high-thread-mobile-find.liquid. The Find on
   desktop does NOT need it: it reuses the option-button path that already existed in
   assets/product-add-to-cart.js. */
(function () {
  // The homepage loads this twice (the interactive environment section and the mobile
  // section both ask for it), so the second execution must be a no-op.
  if (window.HighThreadVariantOptions) return;

  function variantList(product) {
    return product && Array.isArray(product.variants) ? product.variants : [];
  }

  /* The value a variant holds for each option, in option order. Liquid gives us
     variant.options directly; the split on " / " is only a fallback for older payloads
     that shipped nothing but variant.title. */
  function variantValues(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options.map(String);
    return String(variant.title || '').split(' / ');
  }

  /* Shopify names the placeholder option of a product with no real options "Title".
     Showing that as a heading would read as nonsense, and the surfaces all said "Size"
     before this change, so that is what an unnamed option falls back to. */
  function heading(name) {
    var label = String(name == null ? '' : name).trim();
    if (!label || label === 'Title') return 'Size';
    return label;
  }

  /* Normalises product.options into [{ name, values }].
     - options_with_values (what the surfaces embed) is already that shape.
     - product.options from `product | json` is a plain array of names, so the values are
       recovered from the variants.
     - a payload with no options at all falls back to one pseudo-option built from the
       variant titles, which is exactly what the surfaces rendered before Item 15. */
  function options(product) {
    var variants = variantList(product);
    var raw = product && Array.isArray(product.options) ? product.options : [];
    var normalised = [];
    var index;

    for (index = 0; index < raw.length; index += 1) {
      var entry = raw[index];
      if (entry && typeof entry === 'object' && Array.isArray(entry.values)) {
        normalised.push({ name: entry.name, values: entry.values.map(String) });
      } else {
        normalised.push({ name: String(entry), values: collectValues(variants, index) });
      }
    }

    if (!normalised.length && variants.length) {
      var depth = 0;
      variants.forEach(function (variant) {
        depth = Math.max(depth, variantValues(variant).length);
      });
      for (index = 0; index < depth; index += 1) {
        normalised.push({ name: '', values: collectValues(variants, index) });
      }
    }

    return normalised;
  }

  function collectValues(variants, index) {
    var seen = [];
    variants.forEach(function (variant) {
      var value = variantValues(variant)[index];
      if (value && seen.indexOf(value) === -1) seen.push(value);
    });
    return seen;
  }

  function hasOnlyDefaultVariant(product) {
    var variants = variantList(product);
    return variants.length === 1 && variants[0] && variants[0].title === 'Default Title';
  }

  /* Exact match: every option must be chosen and every value must line up. */
  function findVariant(product, values) {
    if (!isComplete(product, values)) return null;

    return variantList(product).filter(function (variant) {
      var candidate = variantValues(variant);
      return values.every(function (value, index) {
        return candidate[index] === value;
      });
    })[0] || null;
  }

  /* Is `value` still worth offering at `index`, given what is already chosen?

     Only the options BEFORE `index` constrain the answer. That is deliberate: if every
     option constrained every other one, a shopper who picked a pair where all the
     alternatives are sold out would find every value in both rows greyed and no way back
     out. Constraining forwards only means the first row is always fully selectable, so
     there is always a route to any combination. */
  function isValueAvailable(product, values, index, value) {
    var chosen = (values || []).slice(0, index);

    return variantList(product).some(function (variant) {
      if (!variant || !variant.available) return false;
      var candidate = variantValues(variant);
      if (candidate[index] !== value) return false;
      return chosen.every(function (chosenValue, chosenIndex) {
        return !chosenValue || candidate[chosenIndex] === chosenValue;
      });
    });
  }

  function isComplete(product, values) {
    var expected = options(product).length;
    if (!expected || !Array.isArray(values) || values.length !== expected) return false;
    return values.every(function (value) { return Boolean(value); });
  }

  /* Human-readable label for a chosen combination, used by the notification modal.
     A single-option product reads exactly as it did before Item 15 ("8"); a set gets its
     option names so "Top 8 / Skirt 10" cannot be mistaken for one confusing number. */
  function describe(product, values) {
    var productOptions = options(product);
    var chosen = (values || []).filter(function (value) { return Boolean(value); });
    if (!chosen.length) return '';

    if (productOptions.length < 2) return chosen.join(' / ');

    return (values || []).map(function (value, index) {
      if (!value) return '';
      var name = heading(productOptions[index] ? productOptions[index].name : '');
      return name + ' ' + value;
    }).filter(function (part) { return Boolean(part); }).join(' / ');
  }

  /* Turns whatever a caller has to hand -- a variant id, an array of option values, or
     nothing -- into an array of option values, so callers do not each need this dance. */
  function toValues(product, preselection) {
    if (Array.isArray(preselection)) return preselection.map(function (value) { return value || ''; });
    if (!preselection) return [];

    var match = variantList(product).filter(function (variant) {
      return variant && String(variant.id) === String(preselection);
    })[0];

    return match ? variantValues(match) : [];
  }

  window.HighThreadVariantOptions = {
    options: options,
    variantValues: variantValues,
    hasOnlyDefaultVariant: hasOnlyDefaultVariant,
    heading: heading,
    findVariant: findVariant,
    isValueAvailable: isValueAvailable,
    isComplete: isComplete,
    describe: describe,
    toValues: toValues
  };
})();
